import React, { useEffect } from 'react';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  FormControl,
  Select,
  Typography,
  IconButton
} from '@mui/material';

import { SxProps } from '@mui/system';
import {
  FormInputLabel,
  MenuProps,
  useStyles
} from '../MultiSelectWithAll/styled';
import Icons from '../icons';
import StyledCheckbox from '../../modules/common/styled/Checkbox';
import {
  CountWrapper,
  InputLabelText,
  SelectAllWrapper,
  SelectedItemChip,
  SelectedItemContainer,
  SelectedItemWrapper
} from './styled';

interface SelectAllProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  hasTopLabel?: boolean;
  required?: boolean;
  formControlMargin?: 'normal' | 'none' | 'dense' | undefined;
  sx?: SxProps;
  placeholder?: string;
}

const SelectAll = ({
  label,
  options,
  selected,
  onChange,
  disabled,
  hasTopLabel = true,
  required,
  sx,
  placeholder
}: SelectAllProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const selectedItemChipRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(selected.length);

  const classes = useStyles();

  const allValues = options.map((option) => option.value);
  const selectedSet = new Set(selected);
  const isAllSelected = selected.length === allValues.length;
  const isIndeterminate = selected.length > 0 && !isAllSelected;

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: string[]; name?: string } }),
    _child?: React.ReactNode
  ) => {
    const value = event.target.value as string[];

    const lastSelected = value[value.length - 1];

    if (lastSelected === 'all') {
      const shouldDeselectAll = selected.length === allValues.length;
      onChange(shouldDeselectAll ? [] : allValues);
    } else {
      onChange(value);
    }
  };

  const calculateVisibleChipCount = (
    selected: string[],
    options: { value: string; label: string }[],
    containerWidth: number
  ) => {
    if (selected.length === 1) {
      return 1;
    }

    const COUNT_BADGE_WIDTH = 60;

    const measurementElement = document.createElement('div');
    measurementElement.style.visibility = 'hidden';
    measurementElement.style.position = 'absolute';
    measurementElement.style.whiteSpace = 'nowrap';
    document.body.appendChild(measurementElement);

    const selectedChipWidths = selected.map((value) => {
      const optionLabel =
        options.find((opt) => opt.value === value)?.label ?? value;

      measurementElement.innerHTML = optionLabel;
      return measurementElement.offsetWidth + 40; // padding + close icon
    });

    document.body.removeChild(measurementElement);

    let totalUsedWidth = 0;
    let numberOfChipsThatFit = 0;

    for (const chipWidth of selectedChipWidths) {
      if (totalUsedWidth + chipWidth + COUNT_BADGE_WIDTH > containerWidth) {
        break;
      }
      totalUsedWidth += chipWidth;
      numberOfChipsThatFit++;
    }

    return numberOfChipsThatFit;
  };

  useEffect(() => {
    const selectedContainer = containerRef.current;
    if (!selectedContainer) return;

    const visibleChipCount = calculateVisibleChipCount(
      selected,
      options,
      selectedContainer.offsetWidth
    );

    setVisibleCount(visibleChipCount);
  }, [selected, options]);

  return (
    <SelectAllWrapper
      sx={{
        ...sx
      }}
    >
      {hasTopLabel && (
        <InputLabelText sx={{ fontWeight: 600 }} required={required}>
          {label}
        </InputLabelText>
      )}
      <FormControl
        ref={containerRef}
        fullWidth
        variant='outlined'
        sx={{ position: 'relative', m: 0, '&.MuiFormControl-root': { m: 0 } }}
      >
        {!hasTopLabel && <FormInputLabel>{label}</FormInputLabel>}
        <Select
          displayEmpty
          multiple
          value={selected}
          onChange={handleChange}
          disabled={disabled}
          MenuProps={{
            ...MenuProps,
            variant: 'menu',
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            MenuListProps: {
              autoFocusItem: false
            }
          }}
          notched={!hasTopLabel}
          renderValue={(selectedValues) => {
            const values = selectedValues as string[];
            const count = values.length;
            if (count === 0) {
              return (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    opacity: 0.8
                  }}
                  noWrap
                >
                  {placeholder || label}
                </Typography>
              );
            }
            return (
              <SelectedItemContainer ref={selectedItemChipRef}>
                <SelectedItemWrapper>
                  {values
                    .slice(0, visibleCount === 0 ? 1 : visibleCount)
                    .map((value) => {
                      const option = options.find((opt) => opt.value === value);

                      return (
                        <SelectedItemChip
                          key={value}
                          maxWidth={
                            selectedItemChipRef.current
                              ? `${
                                  selectedItemChipRef.current.offsetWidth - 100
                                }px`
                              : 'auto'
                          }
                        >
                          <Typography noWrap>
                            {option?.label || value}
                          </Typography>
                        </SelectedItemChip>
                      );
                    })}
                </SelectedItemWrapper>

                {count > 0 && (
                  <CountWrapper>
                    {count}
                    <IconButton
                      disabled={disabled}
                      sx={{ padding: '2px' }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange([]);
                      }}
                    >
                      <Icons.CloseWhiteFilled />
                    </IconButton>
                  </CountWrapper>
                )}
              </SelectedItemContainer>
            );
          }}
        >
          <MenuItem
            value='all'
            classes={{ root: isAllSelected ? classes.selectedAll : '' }}
          >
            <ListItemIcon>
              <StyledCheckbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                classes={{ indeterminate: classes.indeterminateColor }}
              />
            </ListItemIcon>
            <ListItemText
              primary='Select All'
              classes={{ primary: classes.selectAllText }}
            />
          </MenuItem>

          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <ListItemIcon>
                <StyledCheckbox checked={selectedSet.has(option.value)} />
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  sx: { whiteSpace: 'normal' }
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </SelectAllWrapper>
  );
};

SelectAll.defaultProps = {
  disabled: false
};

export default SelectAll;
